import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  Tag,
  Space,
  Typography,
  Avatar,
  Popconfirm,
  message,
  Badge,
  Row,
  Col,
  Statistic,
  Input as AntInput
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  SearchOutlined,
  PlusOutlined,
  TeamOutlined,
  UserAddOutlined
} from '@ant-design/icons';
import { usersService } from '../services/usersService';

const { Title } = Typography;
const { Option } = Select;
const { Search } = AntInput;

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filteredUsers, setFilteredUsers] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [form] = Form.useForm();

  // Загрузка пользователей из Firebase
  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await usersService.getAllUsers();
      setUsers(data);
      setFilteredUsers(data);
    } catch (error) {
      console.error('Error loading users:', error);
      message.error('Ошибка загрузки пользователей');
    } finally {
      setLoading(false);
    }
  };

  // Фильтрация пользователей
  useEffect(() => {
    if (searchText) {
      const filtered = users.filter(user => 
        (user.name && user.name.toLowerCase().includes(searchText.toLowerCase())) ||
        (user.email && user.email.toLowerCase().includes(searchText.toLowerCase())) ||
        (user.phone && user.phone.includes(searchText))
      );
      setFilteredUsers(filtered);
    } else {
      setFilteredUsers(users);
    }
  }, [users, searchText]);

  // Статистика
  const stats = {
    total: users.length,
    active: users.filter(u => u.status === 'active').length,
    parents: users.filter(u => u.role === 'parent').length,
    teachers: users.filter(u => u.role === 'teacher').length
  };

  const columns = [
    {
      title: 'Пользователь',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space>
          <Avatar 
            size={40} 
            icon={<UserOutlined />}
            src={record.avatar}
          />
          <div>
            <div style={{ fontWeight: 'bold' }}>{text}</div>
            <div style={{ fontSize: '12px', color: '#666' }}>
              {record.role === 'parent' ? 'Родитель' : 'Учитель'}
            </div>
          </div>
        </Space>
      ),
    },
    {
      title: 'Контакт',
      key: 'contact',
      render: (_, record) => (
        <div>
          <div style={{ fontSize: '12px' }}>
            <MailOutlined /> {record.email}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            <PhoneOutlined /> {record.phone}
          </div>
        </div>
      ),
    },
    {
      title: 'Роль',
      dataIndex: 'role',
      key: 'role',
      render: (role) => (
        <Tag color={role === 'parent' ? 'blue' : 'green'}>
          {role === 'parent' ? 'Родитель' : 'Учитель'}
        </Tag>
      )
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Badge 
          status={status === 'active' ? 'success' : 'default'} 
          text={status === 'active' ? 'Активен' : 'Неактивен'} 
        />
      )
    },
    {
      title: 'Дети',
      dataIndex: 'children',
      key: 'children',
      render: (children) => (children || 0) > 0 ? `${children} детей` : 'Нет детей'
    },
    {
      title: 'Последний вход',
      dataIndex: 'lastLogin',
      key: 'lastLogin',
      render: (date) => {
        if (!date) return '-';
        if (date && date.toDate) {
          return date.toDate().toLocaleDateString('ru-RU');
        }
        if (date instanceof Date) {
          return date.toLocaleDateString('ru-RU');
        }
        return new Date(date).toLocaleDateString('ru-RU');
      }
    },
    {
      title: 'Действия',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => handleView(record)}
            title="Просмотр"
          />
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            title="Редактировать"
          />
          <Popconfirm
            title="Удалить пользователя?"
            description="Это действие нельзя отменить"
            onConfirm={() => handleDelete(record.id)}
            okText="Да"
            cancelText="Нет"
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              title="Удалить"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handleAdd = () => {
    setEditingUser(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    form.setFieldsValue({
      ...user,
      status: user.status === 'active'
    });
    setIsModalVisible(true);
  };

  const handleView = (user) => {
    Modal.info({
      title: `Информация о пользователе: ${user.name}`,
      width: 500,
      content: (
        <div style={{ padding: '16px 0' }}>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <strong>Email:</strong> {user.email}
            </Col>
            <Col span={12}>
              <strong>Телефон:</strong> {user.phone}
            </Col>
            <Col span={12}>
              <strong>Роль:</strong> {user.role === 'parent' ? 'Родитель' : 'Учитель'}
            </Col>
            <Col span={12}>
              <strong>Статус:</strong> {user.status === 'active' ? 'Активен' : 'Неактивен'}
            </Col>
            <Col span={12}>
              <strong>Количество детей:</strong> {user.children || 0}
            </Col>
            <Col span={12}>
              <strong>Последний вход:</strong> {
                user.lastLogin ? (
                  user.lastLogin.toDate ? 
                    user.lastLogin.toDate().toLocaleDateString('ru-RU') : 
                    new Date(user.lastLogin).toLocaleDateString('ru-RU')
                ) : '-'
              }
            </Col>
            <Col span={24}>
              <strong>Дата регистрации:</strong> {
                user.createdAt ? (
                  user.createdAt.toDate ? 
                    user.createdAt.toDate().toLocaleDateString('ru-RU') : 
                    new Date(user.createdAt).toLocaleDateString('ru-RU')
                ) : '-'
              }
            </Col>
          </Row>
        </div>
      ),
    });
  };

  const handleDelete = async (id) => {
    try {
      await usersService.deleteUser(id);
      setUsers(users.filter(user => user.id !== id));
      setFilteredUsers(filteredUsers.filter(user => user.id !== id));
      message.success('Пользователь удален');
    } catch (error) {
      console.error('Error deleting user:', error);
      message.error('Ошибка удаления пользователя');
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      
      const userData = {
        ...values,
        status: values.status ? 'active' : 'inactive',
        children: values.children || 0,
        role: values.role || 'parent'
      };
      
      if (editingUser) {
        // Обновление существующего пользователя
        await usersService.updateUser(editingUser.id, userData);
        message.success('Пользователь обновлен');
        await loadUsers(); // Перезагружаем список
      } else {
        // Создание нового пользователя
        await usersService.createUser(userData);
        message.success('Пользователь добавлен');
        await loadUsers(); // Перезагружаем список
      }
      
      setIsModalVisible(false);
      form.resetFields();
      setEditingUser(null);
    } catch (error) {
      console.error('Error saving user:', error);
      if (error.message) {
        message.error(error.message);
      } else {
        message.error('Ошибка сохранения пользователя');
      }
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
    setEditingUser(null);
  };

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>Управление пользователями</Title>
        <p style={{ color: '#666', margin: 0 }}>Управляйте пользователями приложения</p>
      </div>

      {/* Статистика */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Всего пользователей"
              value={stats.total}
              prefix={<TeamOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Активных"
              value={stats.active}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Родителей"
              value={stats.parents}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Учителей"
              value={stats.teachers}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Поиск и действия */}
      <Card style={{ marginBottom: '16px' }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} md={8}>
            <Search
              placeholder="Поиск по имени, email или телефону"
              allowClear
              onSearch={setSearchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={12} md={16} style={{ textAlign: 'right' }}>
            <Button
              type="primary"
              icon={<UserAddOutlined />}
              onClick={handleAdd}
            >
              Добавить пользователя
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Таблица */}
      <Card>
        <Table
          columns={columns}
          dataSource={filteredUsers}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} из ${total} пользователей`
          }}
          scroll={{ x: 800 }}
        />
      </Card>

      {/* Модальное окно */}
      <Modal
        title={editingUser ? 'Редактировать пользователя' : 'Добавить пользователя'}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        width={600}
        okText="Сохранить"
        cancelText="Отмена"
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Form.Item
            name="name"
            label="Имя пользователя"
            rules={[{ required: true, message: 'Введите имя' }]}
          >
            <Input placeholder="Введите имя пользователя" />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Введите email' },
              { type: 'email', message: 'Введите корректный email' }
            ]}
          >
            <Input placeholder="Введите email" />
          </Form.Item>

          <Form.Item
            name="phone"
            label="Телефон"
            rules={[{ required: true, message: 'Введите телефон' }]}
          >
            <Input placeholder="+992 90 123 4567" />
          </Form.Item>

          <Form.Item
            name="role"
            label="Роль"
            rules={[{ required: true, message: 'Выберите роль' }]}
          >
            <Select placeholder="Выберите роль">
              <Option value="parent">Родитель</Option>
              <Option value="teacher">Учитель</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="children"
            label="Количество детей"
            rules={[{ required: true, message: 'Введите количество детей' }]}
          >
            <Input type="number" min={0} placeholder="0" />
          </Form.Item>

          <Form.Item
            name="status"
            label="Статус"
            valuePropName="checked"
          >
            <Switch checkedChildren="Активен" unCheckedChildren="Неактивен" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Users;